import React, {
    useState,
    useEffect,
    useContext,
    useCallback,
    useMemo,
} from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { IconButton, Divider, Box, Typography } from '@mui/material'
import { listUsers, listTenantUsers } from '../api'
import {
    getOffset,
    usePagination,
    Pagination,
    UsersIcon,
    MoreVertIcon,
    AddIcon,
} from '../components'
import { AppContext } from '../context'
import { FeedbackContext } from '../feedback'
import { Griddable } from '../grid'
import { translate } from '../i18n'
import { Users, User } from '../model'
import { RouterParams } from '../router'
import UserMenu from './UserMenu'
import UserActivatePopup from './UserActivatePopup'
import UserBlockPopup from './UserBlockPopup'
import UserUnblockPopup from './UserUnblockPopup'
import UserDeletePopup from './UserDeletePopup'
import UsersFilter, { useUsersParams } from './UsersFilter'
import { PatternFormat } from 'react-number-format'
import { phoneFormat } from '../validators'

type Dialog = 'block' | 'unblock' | 'delete'

export default function UsersList({ match }: RouterParams) {
    const history = useHistory()
    const feedbackContext = useContext(FeedbackContext)
    const context = useContext(AppContext)
    const paramTenantId = match.params.tenantId
    const tenantId: string | undefined =
        context.session?.tenant?.id ?? paramTenantId
    const allowedCreate = useMemo(
        () => context.isGrantedAny(['UsersCreate', 'TenantsUsersCreate']),
        [context]
    )

    const query = new URLSearchParams(useLocation().search)
    const params = useUsersParams(query)

    const [status, setStatus] = useState<'loading' | 'loaded' | Error>(
        'loading'
    )
    const [data, setData] = useState<Users | undefined>()
    const [user, setUser] = useState<User>()
    const [activatePopup, setActivatePopup] = useState<boolean>(false)
    const [dialog, setDialog] = useState<'block' | 'unblock' | 'delete'>()

    const clearCallback = useCallback(() => setData(undefined), [])
    const { page, pageSize, onChangedPage, onChangedPageSize } =
        usePagination(clearCallback)

    const retrievePromise = useCallback((): Promise<Users> => {
        const offset = getOffset(page, pageSize)
        const fixedParams = { ...params, pageSize, offset }
        if (tenantId) {
            return listTenantUsers(tenantId, fixedParams)
        }
        return listUsers(fixedParams)
    }, [tenantId, page, pageSize, params])

    const reload = useCallback(() => {
        setStatus('loading')
        retrievePromise()
            .then((response) => {
                setData(response)
                setStatus('loaded')
            })
            .catch((error) => {
                setStatus(error.message)
            })
    }, [retrievePromise])

    useEffect(reload, [reload])

    const showDialog = (dialog: Dialog) => (user: User) => {
        setUser(user)
        setDialog(dialog)
    }

    const onBlocked = () => {
        if (!user) return

        user.blocked = true
        feedbackContext.showSuccess(
            translate('users.block.success', user) as string
        )
        setUser(undefined)
        setDialog(undefined)
    }

    const onUnblocked = () => {
        if (!user) return

        user.blocked = false
        feedbackContext.showSuccess(
            translate('users.unblock.success', user) as string
        )
        setUser(undefined)
        setDialog(undefined)
    }

    const onDelete = () => {
        if (!user) return

        reload()
        feedbackContext.showSuccess(
            translate('users.delete.success', {
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
            }) as string
        )
        setUser(undefined)
        setDialog(undefined)
    }

    const onError = (error: string) => {
        feedbackContext.showError(error)
        setUser(undefined)
        setDialog(undefined)
    }

    const onCancel = () => {
        setUser(undefined)
        setDialog(undefined)
    }

    const showActivatePopup = (user: User) => {
        setUser(user)
        setActivatePopup(true)
    }

    const hideActivatePopup = () => {
        setUser(undefined)
        setActivatePopup(false)
    }

    const onClicked = useCallback(
        (user: User) => {
            history.push(
                paramTenantId
                    ? `/tenants/${paramTenantId}/users/${user.id}/edit`
                    : `/users/${user.id}/edit`
            )
        },
        [history, paramTenantId]
    )

    return (
        <Pagination
            title={translate('users.title')}
            subtitle={data?.tenant ? data.tenant.name : undefined}
            icon={<UsersIcon />}
            page={page}
            pageSize={pageSize}
            count={data ? data.items.length : 0}
            total={data ? data.total : 0}
            onChangedPage={onChangedPage}
            onChangedPageSize={onChangedPageSize}
            buttons={[
                {
                    text: translate('buttons.add'),
                    icon: <AddIcon />,
                    path: match.params.tenantId
                        ? `/tenants/${match.params.tenantId}/users/new`
                        : '/users/new',
                    available: allowedCreate,
                    variant: 'primary',
                },
            ]}
        >
            <UsersFilter roles={data?.metadata.roles ?? []} />
            <Box mx={-2} mt={2}>
                <Divider />
                <Griddable
                    items={data ? data.items : []}
                    loading={status === 'loading'}
                    error={status instanceof Error ? status.message : undefined}
                    empty={translate('users.empty') as string}
                    getId={(user: User) => user.id}
                    clickable={{
                        onClick: onClicked,
                    }}
                    refreshable={{
                        columnId: 'actions',
                        onClick: reload,
                    }}
                    columns={[
                        {
                            title: translate('users.name') as string,
                            converter: (user: User) => user.name,
                            id: 'name',
                        },
                        {
                            title: translate('users.email') as string,
                            converter: (user: User) => user.email,
                            id: 'email',
                        },
                        {
                            title: translate('users.phone') as string,
                            converter: (user: User) =>
                                user.phone ? (
                                    <PatternFormat
                                        value={user.phone}
                                        format={phoneFormat(user.phone)}
                                        displayType="text"
                                        renderText={(text) => (
                                            <Typography variant="body2">
                                                {text}
                                            </Typography>
                                        )}
                                    />
                                ) : (
                                    '---'
                                ),
                            id: 'phone',
                        },
                        {
                            title: translate('users.roles.title') as string,
                            converter: (user: User) =>
                                user.role
                                    ? translate(`users.roles.${user.role.id}`)
                                    : '---',
                            id: 'role',
                        },
                        {
                            title: translate('users.status.title') as string,
                            converter: (user: User) =>
                                translate(
                                    `users.status.${
                                        user.blocked
                                            ? 'blocked'
                                            : user.signup_code
                                            ? 'inactive'
                                            : 'active'
                                    }`
                                ),
                            id: 'status',
                        },
                        {
                            title: (
                                <IconButton
                                    size="small"
                                    style={{ visibility: 'hidden' }}
                                    disabled
                                >
                                    <MoreVertIcon />
                                </IconButton>
                            ),
                            converter: (user: User) => (
                                <UserMenu
                                    user={user}
                                    tenantId={match.params.tenantId}
                                    onBlock={showDialog('block')}
                                    onUnblock={showDialog('unblock')}
                                    onDelete={showDialog('delete')}
                                    onActivated={showActivatePopup}
                                />
                            ),
                            id: 'actions',
                            fixed: 'right',
                            width: 60,
                            textAlign: 'center',
                        },
                    ]}
                />
            </Box>
            {user && activatePopup && (
                <UserActivatePopup user={user} onClose={hideActivatePopup} />
            )}
            {user && dialog === 'block' && (
                <UserBlockPopup
                    user={user}
                    tenantId={tenantId}
                    onCompleted={onBlocked}
                    onError={onError}
                    onCancelled={onCancel}
                />
            )}
            {user && dialog === 'unblock' && (
                <UserUnblockPopup
                    user={user}
                    tenantId={tenantId}
                    onCompleted={onUnblocked}
                    onError={onError}
                    onCancelled={onCancel}
                />
            )}
            {user && dialog === 'delete' && (
                <UserDeletePopup
                    user={user}
                    tenantId={tenantId}
                    onCompleted={onDelete}
                    onError={onError}
                    onCancelled={onCancel}
                />
            )}
        </Pagination>
    )
}
